import React, { useCallback, useEffect, useState } from 'react';
import {
  Phone, MessageCircle, Mail, MapPin, Instagram, Facebook,
  Clock, Store, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';
import { contactAPI } from '../../api/contact';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';

// ── Toast ─────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div className="fixed top-4 left-1/2 z-[300] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 animate-fade-up">
      <div
        className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl"
        style={{
          background: isSuccess ? 'rgba(5,150,105,0.95)' : 'rgba(220,38,38,0.95)',
          border: `1px solid ${isSuccess ? 'rgba(110,231,183,0.3)' : 'rgba(252,165,165,0.3)'}`,
        }}
      >
        {isSuccess
          ? <CheckCircle2 size={16} color="#6ee7b7" />
          : <AlertCircle  size={16} color="#fca5a5" />}
        <p className="text-sm font-medium text-white">{toast.message}</p>
      </div>
    </div>
  );
}

// ── Field row ─────────────────────────────────────────────────
function Field({ label, icon: Icon, name, value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="input-label flex items-center gap-1.5">
        <Icon size={12} style={{ color: '#d4a017' }} />
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input-field"
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
const EMPTY = {
  business_name: '',
  store_hours:   '',
  phone:         '',
  whatsapp:      '',
  email:         '',
  address:       '',
  instagram:     '',
  facebook:      '',
};

export default function ContactSettingPage() {
  const [form,       setForm]       = useState(EMPTY);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [error,      setError]      = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await contactAPI.get();
      if (data.status && data.data) {
        setForm({
          business_name: data.data.business_name ?? '',
          store_hours:   data.data.store_hours   ?? '',
          phone:         data.data.phone         ?? '',
          whatsapp:      data.data.whatsapp      ?? '',
          email:         data.data.email         ?? '',
          address:       data.data.address       ?? '',
          instagram:     data.data.instagram     ?? '',
          facebook:      data.data.facebook      ?? '',
        });
      }
    } catch {
      setError('Failed to load contact settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await contactAPI.update(form);
      if (data.status) {
        showToast('Contact settings saved successfully');
      } else {
        showToast(data.message || 'Failed to save settings', 'error');
      }
    } catch (err) {
      showToast(
        err?.response?.data?.message || 'Something went wrong. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="flex flex-col min-h-0">
        <PageHeader
          title="Contact Info"
          subtitle="Manage what users see on the Contact page"
        />

        <div className="px-4 pb-8">

          {loading ? (
            <div className="flex flex-col gap-3 mt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="skeleton h-14 rounded-2xl" />
              ))}
            </div>
          ) : error ? (
            <div
              className="mt-4 rounded-2xl px-4 py-4 text-sm text-center"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5' }}
            >
              {error}
              <button
                onClick={fetchSettings}
                className="block mx-auto mt-2 text-xs underline underline-offset-2"
                style={{ color: '#fca5a5' }}
              >
                Retry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">

              {/* Business */}
              <div
                className="rounded-2xl p-4 border flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,160,23,0.15)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest"
                   style={{ color: 'rgba(212,160,23,0.7)' }}>
                  Business Info
                </p>
                <Field label="Business Name" icon={Store}   name="business_name" value={form.business_name} onChange={handleChange} placeholder="e.g. Shine Jewellery" />
                <Field label="Store Hours"   icon={Clock}   name="store_hours"   value={form.store_hours}   onChange={handleChange} placeholder="e.g. Mon – Sat, 10 AM – 7 PM" />
              </div>

              {/* Contact */}
              <div
                className="rounded-2xl p-4 border flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,160,23,0.15)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest"
                   style={{ color: 'rgba(212,160,23,0.7)' }}>
                  Contact Details
                </p>
                <Field label="Phone"     icon={Phone}          type="tel"   name="phone"     value={form.phone}     onChange={handleChange} placeholder="+91 XXXXX XXXXX" />
                <Field label="WhatsApp"  icon={MessageCircle}  type="tel"   name="whatsapp"  value={form.whatsapp}  onChange={handleChange} placeholder="+91 XXXXX XXXXX" />
                <Field label="Email"     icon={Mail}           type="email" name="email"     value={form.email}     onChange={handleChange} placeholder="support@example.com" />

                <div className="flex flex-col gap-1.5">
                  <label className="input-label flex items-center gap-1.5">
                    <MapPin size={12} style={{ color: '#d4a017' }} />
                    Store Address
                  </label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Full store address"
                    rows={3}
                    className="input-field resize-none"
                  />
                </div>
              </div>

              {/* Social */}
              <div
                className="rounded-2xl p-4 border flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212,160,23,0.15)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest"
                   style={{ color: 'rgba(212,160,23,0.7)' }}>
                  Social Media
                </p>
                <Field label="Instagram" icon={Instagram} name="instagram" value={form.instagram} onChange={handleChange} placeholder="https://instagram.com/yourpage" />
                <Field label="Facebook"  icon={Facebook}  name="facebook"  value={form.facebook}  onChange={handleChange} placeholder="https://facebook.com/yourpage" />
              </div>

              <Button type="submit" fullWidth loading={submitting}>
                {submitting ? (
                  <span className="flex items-center gap-2 justify-center">
                    <Loader2 size={15} className="animate-spin" /> Saving...
                  </span>
                ) : 'Save Contact Info'}
              </Button>

            </form>
          )}
        </div>
      </div>
    </>
  );
}
